import React, { useState, useEffect, useRef } from 'react';
import { Modal, ModalHeader, ModalBody, Button, Form, FormGroup, Label, Input } from 'reactstrap';
import Loader from '../Loader/Loader';
import ArchivoService from '../../axios_services/archivos.service';
import { Paperclip } from 'lucide-react';

const ModalControlPoi = ({
  registro,
  registros,
  onSave,
  onClose,
  isOpen,
  departamentos,
  usuarioId,
  perfilId
}) => {
  const [loading, setLoading] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [documentoPoi, setDocumentoPoi] = useState(null);
  const fileInputRef = useRef(null);

  const mesesOpciones = [
    { valor: '01', nombre: 'Enero' },
    { valor: '02', nombre: 'Febrero' },
    { valor: '03', nombre: 'Marzo' },
    { valor: '04', nombre: 'Abril' },
    { valor: '05', nombre: 'Mayo' },
    { valor: '06', nombre: 'Junio' },
    { valor: '07', nombre: 'Julio' },
    { valor: '08', nombre: 'Agosto' },
    { valor: '09', nombre: 'Septiembre' },
    { valor: '10', nombre: 'Octubre' },
    { valor: '11', nombre: 'Noviembre' },
    { valor: '12', nombre: 'Diciembre' }
  ];

  const getInitialState = () => ({
    departamento_id: '',
    mes: '',
    anio: new Date().getFullYear().toString(),
    alcance: ''
  });

  const [formData, setFormData] = useState(getInitialState());

  const poiMes = 8.33;
  const meta = 100;
  const resultado = formData.alcance ? ((parseFloat(formData.alcance) / meta) * 100).toFixed(2) : 0;

  const mesSeleccionadoNombre = mesesOpciones.find(m => m.valor === formData.mes)?.nombre || '';

  useEffect(() => {
    if (isOpen) {
      setDocumentoPoi(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      
      if (registro) {
        const [anio, mes] = registro.periodo?.split('-') || ['', ''];
        setFormData({
          departamento_id: registro.departamento_id?.toString() || '',
          mes: mes || '',
          anio: anio || new Date().getFullYear().toString(),
          alcance: ''
        });
        setCurrentId(null);
      } else {
        setFormData({
          ...getInitialState(),
          anio: new Date().getFullYear().toString()
        });
        setCurrentId(null);
      }
    }
  }, [registro, isOpen]);

  useEffect(() => {
    if (isOpen && formData.departamento_id && formData.mes && formData.anio && registros) {
      const periodo = `${formData.anio}-${formData.mes}`;
      const existing = registros.find(r =>
        r.departamento_id == formData.departamento_id &&
        r.periodo === periodo
      );

      if (existing) {
        setFormData(prev => ({ ...prev, alcance: existing.alcance }));
        setCurrentId(existing.control_poi_id);
      } else {
        setFormData(prev => ({ ...prev, alcance: '' }));
        setCurrentId(null);
      }
    }
  }, [formData.departamento_id, formData.mes, formData.anio, isOpen, registros]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === 'file') {
      setDocumentoPoi(files && files[0] ? files[0] : null);
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value
      }));
    }
  };

  const handleSubmit = async () => {
    try {
      if (!formData.departamento_id || !formData.mes || !formData.anio || formData.alcance === '') {
        alert('Por favor, complete todos los campos obligatorios.');
        return;
      }

      if (isNaN(formData.alcance) || parseFloat(formData.alcance) < 0 || parseFloat(formData.alcance) > 100) {
        alert('Por favor, ingrese un alcance válido entre 0 y 100.');
        return;
      }

      const periodo = `${formData.anio}-${formData.mes}`;

      const dataToSave = {
        departamento_id: parseInt(formData.departamento_id, 10),
        alcance: parseFloat(formData.alcance),
        periodo: periodo,
        estado: 1
      };

      if (currentId) {
        dataToSave.control_poi_id = currentId;
      }

      const savedRegistro = await onSave(dataToSave);
      const targetId = currentId || savedRegistro?.control_poi_id;

      if (documentoPoi && targetId) {
        await ArchivoService.guardarDocumentoPoi(documentoPoi, targetId, usuarioId);
      }

      onClose(true);

    } catch (error) {
      console.error("Error al guardar:", error);
      const errorMsg = error.response?.data?.message || 'Ocurrió un error al guardar.';
      alert(`Error: ${errorMsg}`);
    }
  };

  return (
    <Modal isOpen={isOpen} className="custom-modal" backdrop="static">
      <ModalHeader className="modal-header">
        <div className="titulo">
          <h1>{registro ? 'EDITAR REGISTRO' : 'NUEVO REGISTRO'}</h1>
          <Button className="close-btn" onClick={() => onClose()}>X</Button>
        </div>
      </ModalHeader>
      <div className="modal-body-footer">
        <ModalBody className="modal-body">
          <Form className="fila" onSubmit={(e) => e.preventDefault()}>
            <FormGroup>
              <Label>Departamento:</Label>
              <Input
                className="form-control"
                type="select"
                name="departamento_id"
                value={formData.departamento_id}
                onChange={handleChange}
                required
              >
                <option value="">Seleccione...</option>
                {departamentos
                  ?.filter(dept => {
                    const pid = Number(perfilId);
                    const deptId = Number(dept.tipo_id);
                    if (pid === 8) return true;
                    if (pid === 1) return [4, 6, 7, 8, 9].includes(deptId);
                    if (pid === 26) return [1, 2, 3, 5].includes(deptId);
                    if (pid === 6 || pid === 9) return [6, 7, 8, 9].includes(deptId);
                    return false;
                  })
                  .map((dept) => (
                    <option key={dept.tipo_id} value={dept.tipo_id}>
                      {dept.descripcion}
                    </option>
                  ))}
              </Input>
            </FormGroup>

            <FormGroup>
              <Label>Año:</Label>
              <Input
                className="form-control"
                type="number"
                name="anio"
                value={formData.anio}
                onChange={handleChange}
                min="2020"
                max="2100"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>Mes:</Label>
              <Input
                className="form-control"
                type="select"
                name="mes"
                value={formData.mes}
                onChange={handleChange}
                required
              >
                <option value="">Seleccione...</option>
                {mesesOpciones.map((mes) => (
                  <option key={mes.valor} value={mes.valor}>
                    {mes.nombre}
                  </option>
                ))}
              </Input>
            </FormGroup>

            <FormGroup>
              <Label>POI {mesSeleccionadoNombre}:</Label>
              <Input
                className="form-control"
                type="text"
                value={`${poiMes.toFixed(2)}%`}
                disabled
              />
            </FormGroup>

            <FormGroup>
              <Label>Meta:</Label>
              <Input
                className="form-control"
                type="text"
                value="100.00%"
                disabled
              />
            </FormGroup>

            <FormGroup>
              <Label>Alcance (%):</Label>
              <div className="input-group custom-input-group">
                <span className="input-group-text custom-symbol">%</span>
                <Input
                  className="form-control"
                  type="number"
                  name="alcance"
                  value={formData.alcance}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  max="100"
                  placeholder="0.00"
                  required
                />
              </div>
            </FormGroup>

            <FormGroup>
              <Label>Resultado:</Label>
              <Input
                className="form-control"
                type="text"
                value={`${((parseFloat(formData.alcance || 0) * 8.33) / 100).toFixed(2)}%`}
                disabled
              />
            </FormGroup>

            <FormGroup style={{ gridColumn: '1 / -1' }}>
              <Label>Documento POI: <span style={{ color: 'red' }}>*</span></Label>
              <div className="custom-file-upload border p-2" style={{ display: 'flex', alignItems: 'center', backgroundColor: '#ffffff', borderRadius: '30px' }}>
                <Button
                  className="button-custom"
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  style={{ display: 'flex', alignItems: 'center', gap: '5px', marginRight: '15px', fontSize: '11px', padding: '4px 10px', borderRadius: '8px' }}
                >
                  <Paperclip size={14} />
                  {documentoPoi ? 'Cambiar archivo' : 'Adjuntar archivo'}
                </Button>
                <span className="text-muted" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, fontSize: '11px' }}>
                  {documentoPoi ? documentoPoi.name : 'Ningún archivo seleccionado'}
                </span>
                <Input
                  type="file"
                  name="documento_poi"
                  accept=".pdf,.doc,.docx,.jpg,.png"
                  onChange={handleChange}
                  innerRef={fileInputRef}
                  style={{ display: 'none' }}
                />
              </div>
            </FormGroup>

            <FormGroup>
              <div className="button-container">
                <Button
                  className="button-custom"
                  onClick={async () => {
                    if (window.confirm('¿Está seguro de guardar este registro?')) {
                      await handleSubmit();
                    }
                  }}
                >
                  GUARDAR
                </Button>
              </div>
            </FormGroup>
          </Form>
        </ModalBody>
      </div>
    </Modal>
  );
};

export default ModalControlPoi;